import {
    AnyAction,
    FileExtension,
    GenericUpload,
    ImagePreviewType,
    ImageProcessingState,
    ImageType,
    LoadingErrorMaybe,
} from "@nova/constants"
import { get } from "nodash"
import { findEnumKeyByValue, trimMiddle } from "@nova/util-strings"
import { formatBytes } from "@nova/format"
import { extractInformationFromUpload, UploadInformation } from "../module"
import { ButtonDestructive } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { createContext, useCallback, useEffect, useState } from "react"
import { WorkspaceSpinner } from "features/data-management/workspace/WorkspaceSpinner"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { setActiveImageId } from "features/map/layers/slice"
import { ClickAwayListener } from "@nova/component-click-away-listener"
import { stagedFiles } from "@nova/slice-multifile-upload"
import { selectImagePreviewContext } from "features/map/layers/selectors"
import { convert16BitImageToVisibleImage } from "features/data-management/hotspots/editor/infrared-image/module"
import { useImagePreviewModalContext } from "./useImagePreviewModalContext"
import { RootState } from "integrations/redux"

export type CreateImagePreviewModalProps = {
    imagePreviewType: ImagePreviewType
    deleteUpload: ({ uploadId, setId }: { uploadId: string; setId: string }) => AnyAction
    // Deletes an uploaded upload, one that exists in the Protobuf file and is no longer just staged/staging.
    deleteUploadedUpload: ({
        id,
        context,
    }: {
        id: string
        context: { flightId: string }
    }) => AnyAction
    selectImageById: (id: string) => (state: AnyAction) => GenericUpload | undefined
    selectJobByFlightId: (flightId: string) => (state: AnyAction) => AnyAction
    selectUploadsForSet: (
        flightId: string
    ) => (s: RootState) => LoadingErrorMaybe<AnyAction>[] | undefined
    selectFlightById: (flightId: string) => (s: RootState) => Layer | undefined
}

export const ImagePreviewModalContext = createContext({} as CreateImagePreviewModalProps)

// returns a named function expression, which React reads the display name from
// oxlint-disable-next-line react/display-name
export function CreateImagePreviewModal(
    createImagePreviewModalProps: CreateImagePreviewModalProps
) {
    return function ImagePreviewModal() {
        return (
            <ImagePreviewModalContext.Provider value={{ ...createImagePreviewModalProps }}>
                <GenericImagePreviewModal />
            </ImagePreviewModalContext.Provider>
        )
    }
}
import { removeLocalUsage } from "features/data-management/workspace/subscriptions/module"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { supported16BitImageTypes } from "features/job-management/uploader/constants"
import { selectSnapshotOpen } from "plugins/PDFv2/PDFSlice"
import { Layer } from "@nova/protobuf"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"

export const GenericImagePreviewModal = () => {
    const { isEditor } = useWorkspacePermissions()
    const {
        imagePreviewType,
        deleteUpload,
        deleteUploadedUpload,
        selectImageById,
        selectJobByFlightId,
        selectUploadsForSet,
        selectFlightById,
    } = useImagePreviewModalContext()
    const dispatch = useAppDispatch()

    const pdfSnapshotIsOpen = useAppSelector(selectSnapshotOpen())

    const imagePreviewContext = useAppSelector(selectImagePreviewContext())
    const selectedId = imagePreviewContext?.id ?? ""
    const selectedImage = useAppSelector(selectImageById(selectedId))
    const upload = selectedImage as GenericUpload | undefined
    const uploadFailed = !!selectedImage && "error" in selectedImage

    const flightId = get(upload, "flightId", "")
    const fileName = get(upload, "fileName", "")
    const size = get(upload, "size", 0)
    const s3Key = get(upload, "s3Key", "")
    const s3KeyLR = upload?.s3KeyLR
    const imageTypeNum = get(upload, "imageType", 1)
    const imageType = findEnumKeyByValue(ImageType, imageTypeNum)
    const job = useAppSelector(selectJobByFlightId(flightId))
    const flight = useAppSelector(selectFlightById(flightId))
    const isDemo = !!flight?.demoId

    const uploads = useAppSelector(selectUploadsForSet(flightId))

    const [focusedIndex, setFocusedIndex] = useState<number | null>(
        uploads?.findIndex((u) => u.id === upload?.id) || null
    )

    useEffect(() => {
        return () => {
            dispatch(setActiveImageId())
        }
    }, [dispatch])

    useEffect(() => {
        const newIndex = uploads?.findIndex((u) => u.id === upload?.id) || null
        setFocusedIndex(newIndex)
    }, [upload?.id, uploads])

    //sets up keyboard handlers so we can arrow navigate between flight path points
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!uploads?.length) return

            const maxIndex = uploads.length - 1

            const updateSelection = (newIndex: number) => {
                setFocusedIndex(newIndex)

                dispatch(
                    setActiveImageId({
                        ids: { [uploads[newIndex].id]: true },
                        type: imagePreviewType,
                    })
                )
            }

            switch (e.key) {
                case "ArrowLeft":
                    e.preventDefault()
                    if (focusedIndex! > 0) updateSelection(focusedIndex! - 1)
                    break
                case "ArrowRight":
                    e.preventDefault()
                    if (focusedIndex! < maxIndex) updateSelection(focusedIndex! + 1)
                    break
            }
        }
        window.addEventListener("keydown", handleKeyDown)
        return () => window.removeEventListener("keydown", handleKeyDown)
    }, [focusedIndex, uploads, imagePreviewType, dispatch])

    const handleDeleteImage = useCallback(() => {
        if (!flightId || !upload) return
        const state = get(upload, "state") as ImageProcessingState
        if (state === ImageProcessingState.UploadDone || state === ImageProcessingState.IngestDone)
            dispatch(deleteUploadedUpload({ id: upload.id, context: { flightId } }))
        else dispatch(deleteUpload({ setId: flightId, uploadId: upload.id }))
        if (size) removeLocalUsage(flightId, size)
    }, [upload, flightId, size, deleteUpload, deleteUploadedUpload, dispatch])

    if (!flightId || !imagePreviewContext || !upload || pdfSnapshotIsOpen) return null
    const stagedFile = stagedFiles[upload?.id]
    return (
        <ClickAwayListener
            onClickAway={(e) => {
                if (!("clickedFlightpathImage" in e)) dispatch(setActiveImageId())
            }}
            className="flex flex-col gap-3 max-w-[288px]"
        >
            <PreviewImage
                key={upload.id}
                stagedFile={stagedFile}
                fileName={fileName}
                s3Key={s3KeyLR || s3Key}
                isDemo={!!flight?.demoId}
                uploadFailed={uploadFailed}
            />
            <div>
                <h5 className="text-gray-0 mb-0.5"> {trimMiddle(fileName, { maxLength: 27 })}</h5>
                {imageType && size && (
                    <div className="flex mono text-gray-300 gap-1">
                        <small className="uppercase">{imageType}</small>
                        <small>{`image - ${formatBytes(size)}`}</small>
                    </div>
                )}
            </div>
            <div>
                <h5 className="text-gray-300 mb-1">INFORMATION</h5>
                {Object.values(UploadInformation).map((info, idx) => {
                    const data = extractInformationFromUpload(upload, info) as string
                    if (!data) return null
                    return <InformationRow key={idx} title={info} value={data} />
                })}
            </div>
            {!!flightId && !job && isEditor && !isDemo && (
                <ButtonDestructive
                    className="!pl-3"
                    LeftIcon={Icons.Trash}
                    isTransparent
                    onClick={handleDeleteImage}
                />
            )}
        </ClickAwayListener>
    )
}

export const InformationRow = ({ title, value }: { title: string; value: string }) => {
    return (
        <div className="flex items-center justify-between py-1 border-b border-gray-700">
            <small className="text-gray-100">{title}</small>
            <small className="text-gray-0">{value}</small>
        </div>
    )
}

const PreviewImage = ({
    fileName,
    stagedFile,
    s3Key,
    isDemo,
    uploadFailed,
}: {
    fileName: string
    stagedFile?: File
    s3Key: string
    isDemo?: boolean
    uploadFailed?: boolean
}) => {
    const [imageUrl, setImageUrl] = useState<string | undefined>()

    const extension = fileName.split(".").at(-1)?.toLocaleLowerCase()
    const isInfraredImage = extension
        ? supported16BitImageTypes.includes(extension as FileExtension)
        : false

    //IR images must be converted before they can be viewed
    const setInfraredImageURL = useCallback(async (blob: Blob) => {
        try {
            const objectUrl = await convert16BitImageToVisibleImage(await blob.arrayBuffer())
            setImageUrl(objectUrl)
        } catch {
            setImageUrl(URL.createObjectURL(blob))
        }
    }, [])

    const fetchImageFromS3 = useCallback(async () => {
        const blob = await fileManagementApi.fetchFile(s3Key, { isDemo }).then((res) => res.blob())
        if (isInfraredImage) {
            setInfraredImageURL(blob)
            return
        }
        setImageUrl(URL.createObjectURL(blob))
    }, [s3Key, isInfraredImage, setInfraredImageURL, isDemo])

    useEffect(() => {
        if (stagedFile) {
            if (isInfraredImage) setInfraredImageURL(stagedFile)
            else setImageUrl(URL.createObjectURL(stagedFile))
            return
        }
        // A failed upload was never stored, so fetching its s3Key would only 403.
        if (fileName && !uploadFailed) fetchImageFromS3()
    }, [fetchImageFromS3, stagedFile, fileName, isInfraredImage, setInfraredImageURL, uploadFailed])

    if (!fileName) return null
    return (
        <div className="rounded-md border border-white-300 overflow-hidden">
            {imageUrl ? (
                <img src={imageUrl} alt="upload" className="max-w-[250px]" />
            ) : (
                <div className="h-[200px] w-[250px]">
                    <WorkspaceSpinner className="!mt-0" />
                </div>
            )}
        </div>
    )
}
